import React, { useEffect, useState } from "react";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import CustomFilters from "../../components/CustomFilters/CustomFilters";
import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { EvaluationAttempt } from "../../models/EvaluationAttempt";
import { Pole } from "../../models/Pole";
import { Program } from "../../models/Program";
import { EXAM_STATUS_ICON, getExamStatusIcon, getStatusColor, normalizeStatus } from "../../services/ExamStatus";
import { validateUserPermission } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { fetchFlags, fetchPoles, fetchPrograms, format, languages, statuses } from "./EvaluationAttempts.function";
import "./EvaluationAttempts.scss";

const EvaluationAttempts = () => {
  const { language, permissions } = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(searchParams.get("status")?.split(",") || []);
  const [selectedIntensities, setSelectedIntensities] = useState<string[]>(
    searchParams.get("format")?.split(",") || []
  );
  const [selectedUserLanguages, setSelectedUserLanguages] = useState<string[]>(
    searchParams.get("language")?.split(",") || []
  );
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(searchParams.get("program")?.split(",") || []);
  const [selectedPoles, setSelectedPoles] = useState<string[]>(searchParams.get("pole")?.split(",") || []);
  const [selectedFlags, setSelectedFlags] = useState<string[]>(searchParams.get("flag")?.split(",") || []);
  const [poles, setPoles] = useState<Pole[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [flags, setFlags] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCustomFilter, setActiveCustomFilter] = useState<string>("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeFilterCount = [
    selectedStatuses.length > 0,
    selectedIntensities.length > 0,
    selectedUserLanguages.length > 0,
    selectedPrograms.length > 0,
    selectedPoles.length > 0,
    selectedFlags.length > 0,
  ].filter(Boolean).length;

  const [loading, setLoading] = useState<{
    poles: boolean;
    programs: boolean;
    flags: boolean;
  }>({
    poles: false,
    programs: false,
    flags: false,
  });

  const _handleApplyCustomFilter = (
    filters: {
      statuses: string[];
      formats: string[];
      languages: string[];
      poles: string[];
      programs: string[];
      flags: string[];
    },
    filterName?: string
  ) => {
    setSelectedStatuses(filters.statuses);
    setSelectedIntensities(filters.formats);
    setSelectedUserLanguages(filters.languages);
    setSelectedPoles(filters.poles);
    setSelectedPrograms(filters.programs);
    setActiveCustomFilter(filterName || "");
    setSelectedFlags(filters.flags);
  };

  const _unselectFilter = () => {
    setActiveCustomFilter("");
  };

  useEffect(() => {
    if (validateUserPermission([{ route: "/poles", methods: ["GET"] }], permissions)) {
      fetchPoles(setPoles, language, (value: boolean) => setLoading((prev) => ({ ...prev, poles: value })));
    }
    if (validateUserPermission([{ route: "/programs", methods: ["GET"] }], permissions)) {
      fetchPrograms(setPrograms, language, (value: boolean) => setLoading((prev) => ({ ...prev, programs: value })));
    }
    if (validateUserPermission([{ route: "/flags", methods: ["GET"] }], permissions)) {
      fetchFlags(setFlags, language, (value: boolean) => setLoading((prev) => ({ ...prev, flags: value })));
    }
  }, [language, permissions]);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());

    setSelectedStatuses(params.status?.split(",") || []);
    setSelectedIntensities(params.format?.split(",") || []);
    setSelectedUserLanguages(params.language?.split(",") || []);
    setSelectedPoles(params.pole?.split(",") || []);
    setSelectedPrograms(params.program?.split(",") || []);
    setSelectedFlags(params.flag?.split(",") || []);
    setSearchQuery(params.search || "");

    const noParams = Object.keys(params).length === 0;

    const isDefaultNavbarFilter =
      params.status === "CORRECTION_PENDING,PRE_CORRECTED" &&
      !params.format &&
      !params.language &&
      !params.pole &&
      !params.program;

    if (noParams || isDefaultNavbarFilter) {
      setActiveCustomFilter("");
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const params: Record<string, string> = {};

    if (selectedStatuses.length > 0) {
      params.status = selectedStatuses.join(",");
    }
    if (selectedIntensities.length > 0) {
      params.format = selectedIntensities.join(",");
    }
    if (selectedUserLanguages.length > 0) {
      params.language = selectedUserLanguages.join(",");
    }
    if (selectedPoles.length > 0) {
      params.pole = selectedPoles.join(",");
    }
    if (selectedPrograms.length > 0) {
      params.program = selectedPrograms.join(",");
    }
    if (selectedFlags.length > 0) {
      params.flag = selectedFlags.join(",");
    }
    if (searchQuery) {
      params.search = searchQuery;
    }

    setSearchParams(params);
  }, [
    selectedStatuses,
    selectedIntensities,
    selectedUserLanguages,
    selectedPoles,
    selectedPrograms,
    selectedFlags,
    searchQuery,
    setSearchParams,
  ]);

  const _generateRemoteUrl = () => {
    const filters = [];
    if (selectedStatuses.length > 0) {
      filters.push(`status=${selectedStatuses.join(",")}`);
    }
    if (selectedIntensities.length > 0) {
      filters.push(`format=${selectedIntensities.join(",")}`);
    }
    if (selectedUserLanguages.length > 0) {
      filters.push(`language=${selectedUserLanguages.join(",")}`);
    }
    if (selectedPoles.length > 0) {
      filters.push(`pole=${selectedPoles.join(",")}`);
    }
    if (selectedPrograms.length > 0) {
      filters.push(`program=${selectedPrograms.join(",")}`);
    }
    if (selectedFlags.length > 0) {
      filters.push(`flag=${selectedFlags.join(",")}`);
    }
    if (searchQuery) {
      filters.push(`search=${searchQuery.toLowerCase()}`);
    }

    return `evaluations/attempts${filters.length > 0 ? `?${filters.join("&")}` : ""}`;
  };

  const columns = [
    {
      name: translate(language, "PAGES.ATTEMPTS.COLUMNS.EXAM_NAME"),
      key: "evaluation.name",
      sortable: true,
      render: (item: EvaluationAttempt) => {
        let iconClass = "dst-icon-message-question-square";
        switch (item.evaluation.type.toLowerCase()) {
          case "notebook":
            iconClass = "dst-icon-file-code-01";
            break;
          case "upload":
            iconClass = "dst-icon-paperclip";
            break;
          case "html":
            iconClass = "dst-icon-code-02";
            break;
        }
        return (
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center">
              <span className={`${iconClass} me-2`} />
              {item.evaluation.name || "N/A"}
            </div>
          </div>
        );
      },
    },
    {
      name: (
        <div className="d-flex align-items-center">
          {translate(language, "PAGES.ATTEMPTS.COLUMNS.DEADLINE.TITLE")}
          <OverlayTrigger
            key="overlay-trigger-deadline-info"
            placement="bottom"
            overlay={
              <Tooltip aria-label="Extra time">
                {translate(language, "PAGES.ATTEMPTS.COLUMNS.DEADLINE.EXPLAIN")}
              </Tooltip>
            }
          >
            <span className="clickable dst-icon-info-circle ms-2" />
          </OverlayTrigger>
        </div>
      ),
      key: "deadline",
      render: (item: EvaluationAttempt) => {
        if (["correction_pending", "being_corrected"].includes(item.status.toLowerCase()) && item.deadline) {
          const deadline = new Date(item.deadline);

          if (!isNaN(deadline.getTime())) {
            return (
              <div
                className={`d-flex align-items-center ${
                  deadline.getTime() <= new Date().getTime() ? "text-danger" : ""
                }`}
              >
                {formatDateHumanly(language, item.deadline)}
              </div>
            );
          } else {
            return translate(language, "PAGES.ATTEMPTS.COLUMNS.DEADLINE.INVALID_DATE");
          }
        } else {
          return "N/A";
        }
      },
    },
    {
      name: translate(language, "PAGES.ATTEMPTS.COLUMNS.END_DATE.TITLE"),
      key: "dt_ended",
      sortable: true,
      render: (item: EvaluationAttempt) => {
        if (item.dt_ended) {
          const dtEnded = new Date(item.dt_ended);

          if (!isNaN(dtEnded.getTime())) {
            return <div className="d-flex align-items-center">{formatDateHumanly(language, item.dt_ended)}</div>;
          } else {
            return translate(language, "PAGES.ATTEMPTS.COLUMNS.END_DATE.INVALID_DATE");
          }
        } else {
          return "N/A";
        }
      },
    },
    {
      name: translate(language, "PAGES.ATTEMPTS.COLUMNS.STATUS"),
      key: "status",
      render: (item: EvaluationAttempt) => {
        const normalizedStatus = normalizeStatus(item.status);
        return (
          <div className="d-flex align-items-center">
            {normalizedStatus ? getExamStatusIcon(normalizedStatus) : EXAM_STATUS_ICON.default}
            <span className={getStatusColor(item.status)}>{item.status || "N/A"}</span>
          </div>
        );
      },
    },
    {
      name: translate(language, "PAGES.ATTEMPTS.COLUMNS.LEARNERS"),
      key: "user.full_name",
      sortable: true,
      render: (item: EvaluationAttempt) => (
        <div className="d-flex align-items-center clickable-cell">
          {item.user.full_name} <span className="dst-icon-link-01 ms-1" />
        </div>
      ),
      onCellClick: (event: React.MouseEvent, item: EvaluationAttempt) =>
        completeNavigate(event, navigate, `/user/${item.user.id}`),
    },
    {
      name: translate(language, "GLOBAL.FLAGS"),
      key: "flags",
      render: (elem: any) => {
        const user = elem.user;
        const allFlags = [
          user.has_extra_time && { icon_name: "clock-stopwatch", name: "Extra Time" },
          ...(user.flags || []),
        ].filter(Boolean);

        if (allFlags.length === 0) {
          return "N/A";
        }

        const remainingFlags = allFlags.slice(3);
        return (
          <span>
            {allFlags.slice(0, 3).map((flag, index) => (
              <OverlayTrigger
                key={`flag-${index}`}
                trigger={["hover", "focus"]}
                placement="top"
                overlay={<Tooltip id={`tooltip-${flag.name}`}>{flag.name}</Tooltip>}
              >
                <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
              </OverlayTrigger>
            ))}
            {remainingFlags.length > 0 && (
              <OverlayTrigger
                trigger={["hover", "focus"]}
                placement="top"
                overlay={
                  <Popover id={`popover-flags-${elem.id}`}>
                    <Popover.Body>
                      {remainingFlags.map((flag, index) => (
                        <div key={`flag-${index}`} className="d-flex align-items-center">
                          <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
                          <span>{flag.name}</span>
                        </div>
                      ))}
                    </Popover.Body>
                  </Popover>
                }
              >
                <span className="rounded light-blue-background blue-font p-1">+{remainingFlags.length}</span>
              </OverlayTrigger>
            )}
          </span>
        );
      },
    },
  ];

  const _clearAllFilters = () => {
    setSelectedPrograms([]);
    setSelectedStatuses([]);
    setSelectedUserLanguages([]);
    setSelectedIntensities([]);
    setSelectedPoles([]);
    setSelectedFlags([]);
    setSearchQuery("");
    _unselectFilter();
  };

  const ActiveFilterChips = () => (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      {activeCustomFilter && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {activeCustomFilter}
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              _clearAllFilters();
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedStatuses.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.ATTEMPTS.FILTER.STATUS")} ({selectedStatuses.length})
          <span
            className="ms-1 clickable"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedStatuses([]);
              _unselectFilter();
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedIntensities.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "GLOBAL.FORMAT")} ({selectedIntensities.length})
          <span
            className="ms-1 clickable"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIntensities([]);
              _unselectFilter();
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedUserLanguages.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.ATTEMPTS.FILTER.LANGUAGE")} ({selectedUserLanguages.length})
          <span
            className="ms-1 clickable"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedUserLanguages([]);
              _unselectFilter();
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedPrograms.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.ATTEMPTS.FILTER.PROGRAM")} ({selectedPrograms.length})
          <span
            className="ms-1 clickable"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPrograms([]);
              _unselectFilter();
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedPoles.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.ATTEMPTS.FILTER.POLE")} ({selectedPoles.length})
          <span
            className="ms-1 clickable"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPoles([]);
              _unselectFilter();
            }}
          >
            ✕
          </span>
        </span>
      )}
      {selectedFlags.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "GLOBAL.FLAG")} ({selectedFlags.length})
          <span
            className="ms-1 clickable"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFlags([]);
              _unselectFilter();
            }}
          >
            ✕
          </span>
        </span>
      )}
      {activeFilterCount > 0 && (
        <span className="clickable dark-gray-font text-decoration-underline xs-regular" onClick={_clearAllFilters}>
          {translate(language, "PAGES.ATTEMPTS.ACTIONS.CLEAR_ALL_FILTER")}
        </span>
      )}
    </div>
  );

  return (
    <React.Fragment>
      <div className="d-flex align-items-center gap-2 p-3 border-bottom flex-wrap">
        <h1 className="mb-0 me-2">{translate(language, "PAGES.ATTEMPTS.TITLE")}</h1>
        <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        <div className="position-relative">
          <DstButton
            variant={activeFilterCount > 0 ? "primary" : "light"}
            btnImageBefore={<span className="dst-icon-filter-lines me-2" />}
            value={translate(language, "PAGES.ATTEMPTS.FILTER.TITLE")}
            clickFunction={() => setDrawerOpen(true)}
          />
          {activeFilterCount > 0 && (
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
              {activeFilterCount}
            </span>
          )}
        </div>
      </div>
      <div className="p-3">
        <div className="gap-3 d-flex flex-column">
          <ActiveFilterChips />
        </div>
        <DstLiveTable
          remote={_generateRemoteUrl()}
          remoteKey="evaluation_attempts_list"
          columns={columns}
          onRowClick={(event, item: any) =>
            ["CREATED", "IN_PROGRESS"].includes(item.status)
              ? null
              : completeNavigate(event, navigate, `/evaluation-attempt/${item.id}`)
          }
          pagination
          itemsPerPage={10}
          paginationResizeOptions={[10, 50, 100]}
          paginationAlwaysShowFooter
        />
      </div>
      {drawerOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 overlay-backdrop"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div
        className={`drawer-right position-fixed top-0 end-0 h-100 white-background d-flex flex-column ${
          drawerOpen ? "is-open" : ""
        }`}
      >
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
          <span className="s-bold h5 mb-0">{translate(language, "PAGES.ATTEMPTS.FILTER.TITLE")}</span>
          <DstButton
            variant="light"
            value=""
            btnImageBefore={<span className="dst-icon-x-close" />}
            clickFunction={() => setDrawerOpen(false)}
          />
        </div>
        <div className="p-3 d-flex flex-column gap-3 overflow-auto flex-grow-1">
          <div className="d-flex flex-column gap-2">
            <span className="xs-regular dark-gray-font text-uppercase">
              {translate(language, "PAGES.ATTEMPTS.FILTER.TITLE")}
            </span>
            <MultiFilterDropdown
              tags={format}
              defaultValue={selectedIntensities}
              onSelectTags={(elem) => {
                setSelectedIntensities(elem as string[]);
                _unselectFilter();
              }}
              showSearchBar
              inputTitle={translate(language, "GLOBAL.FORMAT")}
              language={language}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              tags={languages}
              defaultValue={selectedUserLanguages}
              onSelectTags={(elem) => {
                setSelectedUserLanguages(elem as string[]);
                _unselectFilter();
              }}
              showSearchBar
              inputTitle={translate(language, "PAGES.ATTEMPTS.FILTER.LANGUAGE")}
              language={language}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              tags={statuses}
              defaultValue={selectedStatuses}
              onSelectTags={(elem) => {
                setSelectedStatuses(elem as string[]);
                _unselectFilter();
              }}
              showSearchBar
              inputTitle={translate(language, "PAGES.ATTEMPTS.FILTER.STATUS")}
              language={language}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              tags={poles.map((pole) => ({
                value: pole.id.toString(),
                display: pole.name,
              }))}
              defaultValue={selectedPoles}
              onSelectTags={(elem) => {
                setSelectedPoles(elem as string[]);
                _unselectFilter();
              }}
              showSearchBar
              inputTitle={translate(language, "PAGES.ATTEMPTS.FILTER.POLE")}
              language={language}
              mock={loading.poles}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              tags={programs.map((program) => ({
                value: program.id.toString(),
                display: program.name,
              }))}
              defaultValue={selectedPrograms}
              onSelectTags={(elem) => {
                setSelectedPrograms(elem as string[]);
                _unselectFilter();
              }}
              showSearchBar
              inputTitle={translate(language, "PAGES.ATTEMPTS.FILTER.PROGRAM")}
              language={language}
              mock={loading.programs}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              tags={flags.map((flag: any) => ({
                display: flag.name,
                value: flag.id.toString(),
              }))}
              defaultValue={selectedFlags}
              onSelectTags={(flag) => {
                setSelectedFlags(flag as string[]);
                _unselectFilter();
              }}
              showSearchBar
              inputTitle={translate(language, "GLOBAL.FLAG")}
              language={language}
              mock={loading.flags}
              buttonWidth="280px"
            />
          </div>
          <hr className="my-0" />
          <CustomFilters
            activeCustomFilter={activeCustomFilter}
            currentFilters={{
              flags: selectedFlags,
              formats: selectedIntensities,
              languages: selectedUserLanguages,
              poles: selectedPoles,
              programs: selectedPrograms,
              statuses: selectedStatuses,
            }}
            defaultFilters={{ flags: [], formats: [], languages: [], poles: [], programs: [], statuses: [] }}
            disabled={
              !selectedPrograms.length &&
              !selectedStatuses.length &&
              !selectedUserLanguages.length &&
              !selectedIntensities.length &&
              !selectedPoles.length &&
              !selectedFlags.length
            }
            language={language}
            onApplyFilter={_handleApplyCustomFilter}
            resetActiveFilterOnDelete
            storageKey="nexus-custom-filters-corrections"
          />
        </div>

        {activeFilterCount > 0 && (
          <div className="p-3 border-top">
            <DstButton
              variant="secondary"
              btnClass="w-100"
              value={translate(language, "PAGES.ATTEMPTS.ACTIONS.CLEAR_ALL_FILTER")}
              clickFunction={() => _clearAllFilters()}
            />
          </div>
        )}
      </div>
    </React.Fragment>
  );
};
export default EvaluationAttempts;
